import { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { commonStyles } from "../AuthenticationLayout/authenticationLayoutCommonStyling.tailwind";
import { VERIFY_OTP_PAGE_Styles as styles } from "./verifyOtp.tailwind";
import Button from "../shared/Button/Button";
import OtpInputField from "../shared/Input/OtpInputField/OtpInputField";
import routeConstant, { REDIRECT_ON_LOGIN } from "../../lib/constants/routeConstant";
import { RESEND_OTP_DURATION, VIA_EMAIL } from "../../lib/constants/globalConstant";
import { resendOTP, verifyOTP } from "../../redux/slices/authSlice";
import { storage } from "../../config/storage";
import OverlayLoader from "../shared/Loaders/OverlayLoader/OverlayLoader";
 
const VerifyOTPForm = ( { verifyOtp, resendOTP }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
 
  const { isLoading: isVerifying, resendLoading } = useSelector((state) => state.auth);
 
  const resend_otp_duration = RESEND_OTP_DURATION;
  const [otpValue, setOtpValue] = useState('');
  const [countdown, setCountdown] = useState(resend_otp_duration); // Set initial countdown time
  const [timerActive, setTimerActive] = useState(true); // To control if the timer is active
  const disabled = otpValue.length !== 6 || (isVerifying && !resendLoading);
  // If user tries to directly access this page he/she will be redirected to default path
  if(!state) {
    return <Navigate to={routeConstant.DEFAULT_PAGE} />
  }
 
  // destructure important properties from state
  const { stateValue, loginViaMethod: viaEmail } = state;
 
  // handle verifyOTP function call. In this the api call is made and response is handle
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
   
    /* state has been passed so that the redirection to the next page can be handled accordingly. And the data
       can be fetched from the state and sent to the api */
 
    // State is only passed if there is a new user so that the password can be created.
    // If there is an already existing user then he will be directly redirected to the select-sport page.
    const state = {
      viaEmail,
      stateValue,
    }
 
    const data = (viaEmail === VIA_EMAIL.PHONE_NUMBER)
    ? {
        otp: otpValue,
        phoneNumber: stateValue, // If phone, send it as `phoneNumber`
        viaEmail: VIA_EMAIL.PHONE_NUMBER,
      }
    : {
        otp: otpValue,
        email: stateValue, // If email, send it as `email`
        viaEmail: VIA_EMAIL.EMAIL,
      };
     
  // When the api call is made, onComplete(callback function) is called with a boolean argument
  // If the boolean argument is true, it means the user is a new user and needs to create a password
  // If the boolean argument is false, then the user is an existing user will redirect to Dashboar page
  // In both cases the navigate function is used to navigate to the appropriate page
    const onCompleteNewAccount = (newuser) => {
      if (newuser) {
        // new user created navigate to create-password-page
        navigate(routeConstant.CREATE_PASSWORD_PAGE, { replace: true, state: state });
      } else {
        // user already exists and navigate to appropriate page
 
        // check if we have some route to navigate from local-storage
        const defaultNavigation = storage.fetchFromLocalStorage.navigateOnLogin();
        // if we have no route to navigate from local-storage then navigate to default route
        const navigateToRoute = defaultNavigation ? defaultNavigation : REDIRECT_ON_LOGIN
       
        navigate(navigateToRoute, { replace: true, state: null });
 
        // now clear from local storage
        storage.destroyFromLocalStorage.navigateOnLogin();
      }
    }
   
    await verifyOtp({ data, onCompleteNewAccount });
  };
 
  // handle resend otp
  const handleResendOTP = async () => {
    const data = (viaEmail === VIA_EMAIL.PHONE_NUMBER)
    ? {
        phoneNumber: stateValue, // If phone, send it as `phoneNumber`
        viaEmail: VIA_EMAIL.PHONE_NUMBER,
      }
    : {
        email: stateValue, // If email, send it as `email`
        viaEmail: VIA_EMAIL.EMAIL,
      };
 
      const result = await resendOTP(data); // Dispatch resend action
      if (result?.type?.includes("fulfilled")) {
        setCountdown(resend_otp_duration); // Reset the countdown
        setTimerActive(true); // Start the timer
      }
    };
  
  // Countdown Timer
  useEffect(() => {
    if (timerActive && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
  
      return () => clearInterval(timer);
    } else if (countdown === 0) {
      setTimerActive(false); // Disable the timer when countdown reaches zero
    }
  }, [timerActive, countdown]);
 
  if(!stateValue) {
    // If user tries to directly access this page he/she will be redirected to default path
    return <Navigate to={routeConstant.DEFAULT_PAGE} />
  }
 
  return (
    <div className={commonStyles.sub_container}>
 
      {/* <Alert icon={false} severity="info" >
        <p>OTP has been sent to <b>{stateValue}</b></p>
      </Alert> */}
      {(isVerifying || resendLoading) && <OverlayLoader />}
 
      <form className={`${commonStyles.form_wrpr} !gap-[70px]`} onSubmit={handleVerifyOtp}>
        <div className={`${commonStyles.input_fields_wrpr} !items-center`}>
          <OtpInputField
            otpValue={otpValue}
            setOtpValue={setOtpValue}
          />
          {!timerActive && (
            <button type="button" className={styles.resendOtp} disabled={resendLoading} onClick={handleResendOTP}>{resendLoading ? `Resending...` : `Resend OTP`}</button>
          )}
          {viaEmail === VIA_EMAIL.EMAIL && (
            <p className="text-gray-500 text-sm mt-2">
              OTP sent. Look in inbox or spam.
            </p>
          )}
          {/* Countdown Timer */}
          {timerActive && (
            <span className={`!cursor-default ${styles.resendOtp} hover:!no-underline`}>
              Resend OTP in {countdown}s
            </span>
          )}
        </div>
       
        {/* Login Button */}
        <Button disabled={disabled} variant="primary" type="submit" className={commonStyles.loginButton}>
          {isVerifying && !resendLoading ? `Verifying...` : `Verify OTP`}
        </Button>
      </form>
    </div>
  );
};
 
const mapDispatchToProps = (dispatch) => ({
  verifyOtp: (data) => dispatch(verifyOTP(data)),
  resendOTP: (data) => dispatch(resendOTP(data)),
});
 
export default connect(null, mapDispatchToProps)(VerifyOTPForm);